import { ApiError, buildApiUrl, request } from './api';
import type { DicomClassification, PreparedNiftiAsset } from '../types/dicom';

export type { PreparedNiftiAsset } from '../types/dicom';

export const PREPARED_NIFTI_CACHE_NAME = 'neuroplan-prepared-nifti-v2';

const DICOM_CLASSIFICATIONS: ReadonlySet<DicomClassification> = new Set([
  'T1w',
  'T2w',
  'FLAIR',
  'DWI',
  'SWI',
  'MRA',
  'CT',
  'Unknown',
]);

export interface PrepareStudyNiftiResponse {
  upload_id: string;
  study_instance_uid: string;
  output_directory: string;
  assets: PreparedNiftiAsset[];
  aligned_assets: PreparedNiftiAsset[];
}

export interface NiftiRotationOperation {
  axis: [number, number, number];
  angle_degrees: number;
  translation?: [number, number, number];
}

export interface RotatePreparedNiftiResponse {
  asset: PreparedNiftiAsset;
  invalidated_asset_ids: string[];
  alignment_status: 'pending' | 'running' | 'ready' | 'failed';
}

function isPreparedNiftiAsset(value: unknown): value is PreparedNiftiAsset {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'asset_id' in value &&
      typeof value.asset_id === 'string' &&
      'classification' in value &&
      typeof value.classification === 'string' &&
      DICOM_CLASSIFICATIONS.has(value.classification as DicomClassification) &&
      'series_instance_uid' in value &&
      typeof value.series_instance_uid === 'string' &&
      'file_name' in value &&
      typeof value.file_name === 'string' &&
      'size_bytes' in value &&
      typeof value.size_bytes === 'number' &&
      Number.isSafeInteger(value.size_bytes) &&
      value.size_bytes > 0 &&
      'sha256' in value &&
      typeof value.sha256 === 'string' &&
      'download_url' in value &&
      typeof value.download_url === 'string' &&
      'cache_key' in value &&
      typeof value.cache_key === 'string'
  );
}

function isPrepareStudyNiftiResponse(value: unknown): value is PrepareStudyNiftiResponse {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'upload_id' in value &&
      typeof value.upload_id === 'string' &&
      'study_instance_uid' in value &&
      typeof value.study_instance_uid === 'string' &&
      'output_directory' in value &&
      typeof value.output_directory === 'string' &&
      'assets' in value &&
      Array.isArray(value.assets) &&
      value.assets.length > 0 &&
      value.assets.every(isPreparedNiftiAsset) &&
      'aligned_assets' in value &&
      Array.isArray(value.aligned_assets) &&
      value.aligned_assets.every(isPreparedNiftiAsset)
  );
}

function isRotatePreparedNiftiResponse(value: unknown): value is RotatePreparedNiftiResponse {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'asset' in value &&
      isPreparedNiftiAsset(value.asset) &&
      'invalidated_asset_ids' in value &&
      Array.isArray(value.invalidated_asset_ids) &&
      value.invalidated_asset_ids.every(assetId => typeof assetId === 'string') &&
      'alignment_status' in value &&
      ['pending', 'running', 'ready', 'failed'].includes(value.alignment_status as string)
  );
}

export async function prepareStudyNifti(
  uploadId: string,
  studyInstanceUid: string,
  token: string
): Promise<PrepareStudyNiftiResponse> {
  const response = await request<unknown>(
    `/dicom/uploads/${encodeURIComponent(uploadId)}/studies/${encodeURIComponent(
      studyInstanceUid
    )}/prepare-nifti`,
    { method: 'POST', token }
  );

  if (!isPrepareStudyNiftiResponse(response)) {
    throw new ApiError('Prepared NIfTI data could not be verified.', 500);
  }
  return response;
}

export async function prepareSelectedSeriesNifti(
  uploadId: string,
  studyInstanceUid: string,
  seriesInstanceUid: string,
  token: string
): Promise<PrepareStudyNiftiResponse> {
  const response = await request<unknown>(
    `/dicom/uploads/${encodeURIComponent(uploadId)}/studies/${encodeURIComponent(
      studyInstanceUid
    )}/series/${encodeURIComponent(seriesInstanceUid)}/prepare-nifti`,
    { method: 'POST', token }
  );

  if (!isPrepareStudyNiftiResponse(response) || response.assets.length !== 1) {
    throw new ApiError('Selected NIfTI data could not be verified.', 500);
  }
  return response;
}

export async function prepareSelectedSeriesData(
  uploadId: string,
  studyInstanceUid: string,
  seriesInstanceUid: string,
  token: string
): Promise<PrepareStudyNiftiResponse> {
  return prepareSelectedSeriesNifti(uploadId, studyInstanceUid, seriesInstanceUid, token);
}

export async function prepareRemainingStudyData(
  uploadId: string,
  studyInstanceUid: string,
  token: string
): Promise<PrepareStudyNiftiResponse> {
  return prepareStudyNifti(uploadId, studyInstanceUid, token);
}

export async function startRemainingUploadAlignment(
  uploadId: string,
  token: string
): Promise<void> {
  await request<unknown>(`/dicom/uploads/${encodeURIComponent(uploadId)}/alignment`, {
    method: 'POST',
    token,
  });
}

export async function rotatePreparedNiftiAsset(
  uploadId: string,
  asset: PreparedNiftiAsset,
  operations: NiftiRotationOperation[],
  token: string
): Promise<RotatePreparedNiftiResponse> {
  const response = await request<unknown>(
    `/dicom/uploads/${encodeURIComponent(uploadId)}/prepared-nifti/${encodeURIComponent(
      asset.asset_id
    )}/rotate`,
    {
      method: 'POST',
      token,
      body: JSON.stringify({
        source_sha256: asset.sha256,
        operations,
      }),
    }
  );

  if (!isRotatePreparedNiftiResponse(response)) {
    throw new ApiError('Saved NIfTI data could not be verified.', 500);
  }
  return response;
}

function getPreparedNiftiDownloadUrl(path: string) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const apiPrefix = '/api/v1';
  return buildApiUrl(path.startsWith(apiPrefix) ? path.slice(apiPrefix.length) : path);
}

export async function cachePreparedNiftiAsset(
  asset: PreparedNiftiAsset,
  token: string
): Promise<void> {
  if (typeof caches === 'undefined') {
    throw new ApiError('Browser NIfTI cache is unavailable.', 500);
  }

  const downloadUrl = getPreparedNiftiDownloadUrl(asset.download_url);
  const cacheUrl = new URL(asset.cache_key, window.location.origin).toString();
  const cache = await caches.open(PREPARED_NIFTI_CACHE_NAME);
  let existing = await cache.match(cacheUrl);
  if (!existing) {
    existing = await cache.match(downloadUrl);
  }
  if (existing && Number(existing.headers.get('Content-Length')) === asset.size_bytes) {
    return;
  }

  const response = await fetch(downloadUrl, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store',
  });
  if (!response.ok) {
    throw new ApiError('Selected NIfTI data could not be downloaded.', response.status);
  }

  const blob = await response.blob();
  if (blob.size !== asset.size_bytes) {
    throw new ApiError('Selected NIfTI download is incomplete.', 500);
  }

  const cachedResponse = new Response(blob, {
    headers: {
      'Content-Type': response.headers.get('Content-Type') || 'application/gzip',
      'Content-Length': String(blob.size),
      'X-NeuroPlan-SHA256': asset.sha256,
      'X-NeuroPlan-Series-UID': asset.series_instance_uid,
    },
  });
  await Promise.all([
    cache.put(downloadUrl, cachedResponse.clone()),
    cache.put(cacheUrl, cachedResponse),
  ]);
}
